// trans.js — translate an element between waypoints, no visible path.
// ---------------------------------------------------------------------
//   trans(uid:mover, to:[b2, [100,200], c3], dur:2, ease:easeInOutQuad,
//         mode:once, trig:playhead)
//
// Waypoints (`to:`):
//   - an element id → move so our centre lands on that element's centre
//   - an [x, y] pair → absolute centre position in the element's own
//     coordinate frame (authored score units)
// The element's own authored position is the implicit starting point.
//
// mode:  once (default — travel the list, stay at the end)
//        loop (…and jump back to the start point, repeat)
//        alternate (walk the list forwards then backwards, forever)
// dur:   beats per leg (number, or a Pseq/Prand pattern per leg); a
//        list [1, 0.25, 2] gives the leg into waypoint i the i-th time
//        (cycled, return-home legs use the first)
// ease:  any anime.js easing name (linear, easeInOutQuad, easeOutElastic…);
//        a list [linear, easeOutBounce] gives the leg into waypoint i the
//        i-th easing (cycled, return-home legs use the first); a pattern
//        Pseq/Prand(…) draws one easing per leg
// hold:  pause after each arrival (beats); a list [0.2, 1, 0.2] gives
//        the arrival at waypoint i the i-th hold (cycled, home arrivals
//        use the first); a Pseq/Prand pattern draws one hold per arrival
//
// osc:   0 off (default), 1/true on, a larger number = throttle in ms.
//        Continuous stream on /trans/<uid> (or oscaddr:):
//          [totalProgress 0-1, legProgress 0-1, x, y, legIndex]
//        plus an immediate /trans/<uid>/waypoint [index] on each arrival
//        (index -1 = arrived back at the home position).
//        osc: also exempts the cue from offscreen pausing.
// oscaddr: override the OSC address.
//
// Movement uses the CSS `translate` property, which composes with
// `transform` — so trans() coexists with rotate()/scale() on one element.

import { isOff } from "../parser/cueUtils.js";
import { registerAnimation, autoStartAnimation } from "./animation.js";
import { isObjectTrig } from "./probeTriggers.js";
import { applyPrestateBeforeStart, applyPrestateOnStart } from "./animShared.js";
import { createHitLabel, shouldCreateHitLabel } from "./animOverlays.js";
import { makeValueGenerator, evalMaybeRandom, isRandomised } from "./audio/audioShared.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { publish, velocityChannels } from "../control/paramBinding.js";
import { animeEase } from "../control/easing.js";
import { secs, secPerBeat } from "./beats.js";
import { later } from "./sequence.js";
import { legForT } from "./animMath.js";   // a sync position back onto a leg

const TAG = "[trans]";

/**
 * A random point inside the element's parent group, in the parent's frame.
 *
 * Inset by the element's own half-size so it lands wholly inside rather than
 * hanging over the edge. Returns null when there is no parent box to work
 * with; the caller then skips the waypoint rather than jumping to 0,0.
 *
 * A propagate() template needs this: every member needs its own destination,
 * and to:[x,y] only takes one absolute pair for the whole group.
 *
 * @param {SVGGraphicsElement} el
 * @returns {{x:number, y:number}|null}
 */
function randomPointInParent(el) {
  // Climb past oscilla anim wrappers: scale()/rotate() insert
  // <g class="oscilla-anim…"> between the element and its authored parent,
  // and a wrapper's bbox is just the element itself — scatter would
  // degenerate to "stay put". Compound propagate templates
  // (scale … rotate … trans(to:[scatter])) hit this on every retrigger.
  let ref = el.parentElement;
  while (ref?.classList && [...ref.classList].some(c => c.startsWith("oscilla-anim"))) {
    ref = ref.parentElement;
  }
  if (!ref?.getBBox) return null;
  let pb;
  try { pb = ref.getBBox(); } catch { return null; }
  if (!pb || !(pb.width > 0) || !(pb.height > 0)) return null;

  // the element's own footprint, converted into the parent's units
  const ctm = ref.getScreenCTM?.();
  const sx = Math.abs(ctm?.a) || 1, sy = Math.abs(ctm?.d) || 1;
  const r = el.getBoundingClientRect();
  const halfW = (r.width / sx) / 2, halfH = (r.height / sy) / 2;

  const spanX = pb.width  - 2 * halfW;
  const spanY = pb.height - 2 * halfH;
  // bigger than the box it lives in: stay put in the middle
  const x = spanX > 0 ? pb.x + halfW + Math.random() * spanX : pb.x + pb.width / 2;
  const y = spanY > 0 ? pb.y + halfH + Math.random() * spanY : pb.y + pb.height / 2;
  return { x, y };
}

function bboxCenterLocal(el) {
  // centre in the element's own user space (pre-transform)
  const b = el.getBBox();
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
}

function centerInFrameOf(targetEl, refEl) {
  // target's centre expressed in refEl's parent coordinate frame,
  // via screen space so nested transforms are handled
  const tb = targetEl.getBoundingClientRect();
  const cx = tb.left + tb.width / 2;
  const cy = tb.top + tb.height / 2;
  const ctm = refEl.parentElement?.getScreenCTM?.() || refEl.getScreenCTM?.();
  if (!ctm) return null;
  const inv = ctm.inverse();
  return { x: cx * inv.a + cy * inv.c + inv.e, y: cx * inv.b + cy * inv.d + inv.f };
}

// An ease value as authored: a name to trim, or the parser's funcCall node for
// a parameterised one (in(1.05)), which must reach animeEase intact.
const keepEase = v => (v && typeof v === "object") ? v : String(v).trim();

export function handleTransCue(ast, el, options = {}) {
  if (!el) return;
  const { fromCueTrigger = false } = options;
  const astArgs = ast?.args || [];
  const k = secPerBeat();   // dur:, hold:, tdelay: are beats

  const cfg = {
    kind: "trans",
    astArgs,
    fromCueTrigger,
    to: null,
    dur: 1,
    durList: null,
    durPattern: null,
    ease: "easeInOutQuad",
    easeList: null,
    easePattern: null,
    mode: "once",
    hold: 0,
    holdList: null,
    holdPattern: null,
    trig: "auto",
    tdelay: 0,
    init: "show",
    uid: null,
    osc: 0,
    oscAddr: null,
    sync: undefined,   // absent = synced; only sync:0 / sync:false opt out
    _anim: null,
  };

  for (const a of astArgs) {
    const key = a.key || a.type;
    const val = a.value;
    switch (key) {
      case "uid": cfg.uid = String(val).trim(); break;
      case "to": cfg.to = Array.isArray(val) ? val : [val]; break;
      case "dur": {
        const d = secs(val, k);
        if (d && d.type === "pattern") cfg.durPattern = d;
        else if (Array.isArray(d)) cfg.durList = d.map(v => Number(evalMaybeRandom(v)) || 1);
        else cfg.dur = Number(evalMaybeRandom(d)) || 1;
        break;
      }
      case "ease":
        // A parameterised easing — ease:in(1.05), ease:[in(2), linear] — arrives
        // as the parser's funcCall node. String() would make it
        // "[object Object]"; animeEase spells it back out instead.
        if (val && val.type === "pattern") cfg.easePattern = val;
        else if (Array.isArray(val)) cfg.easeList = val.map(keepEase);
        else cfg.ease = keepEase(val);
        break;
      case "mode": cfg.mode = String(val).trim().toLowerCase(); break;
      case "hold": {
        const h = secs(val, k);
        if (h && h.type === "pattern") cfg.holdPattern = h;
        else if (Array.isArray(h)) cfg.holdList = h.map(v => Number(v) || 0);
        else cfg.hold = Number(h) || 0;
        break;
      }
      case "trig": cfg.trig = String(val).toLowerCase(); break;
      case "tdelay": cfg.tdelay = Number(secs(val, k)) || 0; break;
      case "init": cfg.init = val; break;
      case "osc": {
        let v = val;
        if (typeof v === "string") {
          const lower = v.toLowerCase();
          if (lower === "true") v = 1;
          else if (lower === "false") v = 0;
        }
        cfg.osc = Number(v) || 0;
        break;
      }
      case "oscaddr":
      case "oscAddr":
        cfg.oscAddr = String(val).trim();   // leading slash = full address, sent as written
        break;
      case "sync":
        cfg.sync = !isOff(val);
        break;
    }
  }

  // Legs drawn from a random pattern or rand() differ per client by design,
  // so drift correction would only snap followers onto a schedule they never
  // had. Off unless sync:1 is authored.
  if (cfg.sync === undefined && astArgs.some(a => isRandomised(a.value))) cfg.sync = false;

  if (!cfg.to || !cfg.to.length) {
    console.warn(TAG, "no to:[…] waypoints for", cfg.uid || el.id);
    return;
  }
  if (!cfg.uid) cfg.uid = el.id || `trans_${Math.random().toString(36).slice(2)}`;

  el._oscillaCfg = cfg;
  applyPrestateBeforeStart(el, cfg);

  const start = () => {
    applyPrestateOnStart(el, cfg);
    runTransSequence(el, cfg);
  };
  cfg._start = start;

  registerAnimation(el, "trans", cfg, start);

  if (shouldCreateHitLabel(cfg)) {
    createHitLabel(el, "trans", cfg.uid, { anchorMode: "center", color: "orange", sizeMode: "scale40" });
  }

  const isScrollMode = window.mode === "scroll";
  // trig:<uid> waits for its source object (probeTriggers) even in page mode
  const shouldStartNow = fromCueTrigger || ((cfg.trig === "auto" || !isScrollMode) && !isObjectTrig(cfg.trig));
  if (shouldStartNow) {
    autoStartAnimation(cfg.uid, () => {
      if (cfg.tdelay > 0) later(start, cfg.tdelay * 1000);   // a step of the sequence: a rewind drops it
      else start();
    }, fromCueTrigger);
  }
}

// The mover carries the translate. It must sit OUTSIDE any oscilla-anim
// wrappers: rotate()/scale() pivot about a fixed point in the element's
// local frame, so translating the element INSIDE their wrapper leaves the
// pivot behind — the element then orbits its authored centre instead of
// spinning in place. An outermost wrapper shifts the whole local frame,
// pivots included, so rotation/scale stay centred on the moving element.
function ensureTransWrapper(el) {
  if (el._oscillaTransWrapper?.isConnected) return el._oscillaTransWrapper;
  if (!(el instanceof SVGElement) || !el.parentElement) return el;
  // climb to the outermost anim wrapper around el (scale/rotate may have
  // wrapped it already — in any order, we go above them all)
  let top = el;
  while (top.parentElement?.classList &&
         [...top.parentElement.classList].some(c => c.startsWith("oscilla-anim"))) {
    top = top.parentElement;
  }
  const w = document.createElementNS("http://www.w3.org/2000/svg", "g");
  w.classList.add("oscilla-anim-trans");
  top.parentElement.insertBefore(w, top);
  w.appendChild(top);
  el._oscillaTransWrapper = w;
  return w;
}

function runTransSequence(el, cfg) {
  // Stop a previous run (retrigger)
  cfg._stopped = false;
  if (el._oscillaTransAnim) {
    el._oscillaTransAnim.pause?.();
    clearTimeout(el._oscillaTransTimer);
    el._oscillaTransAnim = null;
  }

  const svg = el.ownerSVGElement;
  if (!svg) return;

  // Home = the element's authored centre, in its own frame, ignoring any
  // translate we applied previously (reset it so measurements are clean)
  const mover = ensureTransWrapper(el);
  mover.style.translate = "";
  el.style.translate = "";   // clear any pre-wrapper state from older runs
  const home = bboxCenterLocal(el);

  // Resolve waypoints → deltas from home (resolved fresh at start so
  // targets that moved since load are honoured)
  const deltas = [];
  for (const wp of cfg.to) {
    if (Array.isArray(wp) && wp.length >= 2) {
      deltas.push({ dx: Number(wp[0]) - home.x, dy: Number(wp[1]) - home.y });
    } else if (/^scatter(?:\s*\(\s*(\d+)\s*\))?$/i.test(String(wp).trim())) {
      // scatter: a random spot inside the parent group. scatter(N) is N of
      // them; repeating the word does the same. Rolled afresh each time the
      // animation starts, so a rewind gives a new arrangement.
      const n = Number(/\((\s*\d+)\s*\)/.exec(String(wp))?.[1] || 1);
      const here = centerInFrameOf(el, el);
      for (let i = 0; i < Math.max(1, Math.min(n, 32)); i++) {
        const pt = randomPointInParent(el);
        if (!pt || !here) { console.warn(TAG, "scatter: no parent box for", cfg.uid || el.id); break; }
        deltas.push({ dx: pt.x - here.x, dy: pt.y - here.y });
      }
    } else {
      const target = svg.getElementById?.(String(wp)) || document.getElementById(String(wp));
      if (!target) { console.warn(TAG, `waypoint "${wp}" not found`); continue; }
      const tc = centerInFrameOf(target, el);
      const hc = centerInFrameOf(el, el);
      if (!tc || !hc) continue;
      // our current screen centre includes prior translate (just cleared),
      // so hc ≈ home in the parent frame; delta is target - us
      deltas.push({ dx: tc.x - hc.x, dy: tc.y - hc.y });
    }
  }
  if (!deltas.length) return;

  // The box the element travels through: home plus every waypoint. x and y are
  // published normalised inside it, the way o2p normalises inside its path's
  // bbox — a raw score coordinate in the thousands is no use as a control
  // signal, and 0-1 across the actual travel is what a follow() range expects.
  const spanX = [0, ...deltas.map((d) => d.dx)];
  const spanY = [0, ...deltas.map((d) => d.dy)];
  const travel = {
    minX: Math.min(...spanX), w: Math.max(...spanX) - Math.min(...spanX),
    minY: Math.min(...spanY), h: Math.max(...spanY) - Math.min(...spanY),
  };

  const durGen = cfg.durPattern ? makeValueGenerator(cfg.durPattern) : null;
  const driver = { x: 0, y: 0 };
  const N = deltas.length;
  let index = 0;          // next waypoint index
  let direction = 1;      // for alternate
  let paused = false;

  // --- OSC ---------------------------------------------------------
  const oscOn = Number(cfg.osc) > 0;
  const oscThrottle = Number(cfg.osc) > 1 ? Number(cfg.osc) : 30;
  const oscAddr = cfg.oscAddr || `trans/${cfg.uid}`;
  let oscLeg = null;      // { fromTotal, toTotal, index } for the running tween

  const emitOsc = (legT) => {
    if (!oscOn || !oscLeg) return;
    const now = performance.now();
    if (cfg._oscLastSent && now - cfg._oscLastSent < oscThrottle) return;
    cfg._oscLastSent = now;
    const total = oscLeg.fromTotal + (oscLeg.toTotal - oscLeg.fromTotal) * legT;
    sendOSC({
      type: "osc_value",
      addr: oscAddr,
      args: [
        total,                 // 0: progress through the whole waypoint list (0-1)
        legT,                  // 1: progress along the current leg (0-1)
        home.x + driver.x,     // 2: centre x (score units)
        home.y + driver.y,     // 3: centre y (score units)
        oscLeg.index,          // 4: waypoint this leg heads for (-1 = home)
      ],
      timestamp: Date.now(),
    });
  };

  const emitWaypoint = (i) => {
    if (!oscOn) return;
    sendOSC({
      type: "osc_value",
      addr: `${oscAddr}/waypoint`,
      args: [i],
      timestamp: Date.now(),
    }, { immediate: true }); // arrival events bypass the throttle
  };
  // -----------------------------------------------------------------

  const apply = (a) => {
    mover.style.translate = `${driver.x}px ${driver.y}px`;
    const legT = a ? (a.progress ?? 0) : 0;
    // Whole-list progress is what the leader broadcasts and followers
    // correct against (animSync.js); direction matters for mode:alternate.
    if (oscLeg) {
      cfg._currentT = oscLeg.fromTotal + (oscLeg.toTotal - oscLeg.fromTotal) * legT;
      cfg._phase = cfg._currentT;
      cfg._alternateDir = direction;
    }
    if (a) emitOsc(legT);

    // Control plane. The travelling itself is a signal, so a moving bar can drive a
    // parameter the way a fader does. Same quantities the OSC emit sends,
    // minus the waypoint index (publish() carries numbers, not events —
    // arrival is what trig:<uid> is for).
    publish("trans", cfg.uid, {
      t: cfg._currentT ?? 0,
      legT,
      x: travel.w > 0 ? (driver.x - travel.minX) / travel.w : 0,
      y: travel.h > 0 ? (driver.y - travel.minY) / travel.h : 0,
      ...velocityChannels("trans", cfg.uid, cfg, driver.x, driver.y, cfg._legSpan),
    });
  };

  // Span of the leg about to start, for the tvel channel: t counts the whole
  // list, so one leg is 1/N of it and tvel = vel / (legLength * N).
  const legSpan = (toX, toY) =>
    (cfg._legSpan = Math.hypot(toX - driver.x, toY - driver.y) * N);

  // Duration of the leg into waypoint i (-1 = the return-home leg):
  // pattern draws one per leg; a list is indexed by waypoint (cycled,
  // home legs take the first entry); otherwise the single dur value.
  const durFor = (wpIndex) => {
    if (durGen) {
      const v = Number(evalMaybeRandom(durGen.next()));
      return Number.isFinite(v) && v > 0 ? v : cfg.dur;
    }
    if (cfg.durList?.length) {
      return cfg.durList[wpIndex < 0 ? 0 : wpIndex % cfg.durList.length];
    }
    return cfg.dur;
  };

  const easeGen = cfg.easePattern ? makeValueGenerator(cfg.easePattern) : null;
  let lastEase = cfg.ease;
  // Easing for the leg into waypoint i (-1 = the return-home leg):
  // pattern draws one per leg; a list is indexed by waypoint (cycled,
  // home legs take the first entry); otherwise the single ease name.
  const easeFor = (wpIndex) => {
    if (easeGen) {
      const v = easeGen.next();
      if (v !== undefined && v !== null) lastEase = keepEase(evalMaybeRandom(v));
      return lastEase;
    }
    if (cfg.easeList?.length) {
      return cfg.easeList[wpIndex < 0 ? 0 : wpIndex % cfg.easeList.length];
    }
    return cfg.ease;
  };

  const holdGen = cfg.holdPattern ? makeValueGenerator(cfg.holdPattern) : null;
  let lastHold = cfg.hold;
  // Hold after arriving at waypoint i (-1 = arrived back home) — same
  // selection rules as easeFor.
  const holdFor = (wpIndex) => {
    if (holdGen) {
      const v = Number(evalMaybeRandom(holdGen.next()));
      if (Number.isFinite(v) && v >= 0) lastHold = v;
      return lastHold;
    }
    if (cfg.holdList?.length) {
      return cfg.holdList[wpIndex < 0 ? 0 : wpIndex % cfg.holdList.length];
    }
    return cfg.hold;
  };

  let currentTween = null;

  function step() {
    if (paused || cfg._stopped) return;

    if (index >= deltas.length || index < 0) {
      if (cfg.mode === "loop") {
        // jump back to the very start point and repeat
        index = 0;
        oscLeg = { fromTotal: 1, toTotal: 0, index: -1 };
        legSpan(0, 0);
        currentTween = anime.animate(driver, {
          x: 0, y: 0,
          duration: durFor(-1) * 1000, ease: animeEase(easeFor(-1)),
          onUpdate: apply, onComplete: () => { emitWaypoint(-1); hold(step, -1); },
        });
        el._oscillaTransAnim = currentTween;
        return;
      }
      if (cfg.mode === "alternate") {
        // Walking back and just arrived at waypoint 0: one more leg, home.
        // (index went negative in the previous leg's complete.) Reaching the
        // far end instead means turn around; the next stop is the waypoint
        // before the one we are standing on — index already points past it.
        if (index >= 0) {
          direction = -1;
          index = deltas.length - 2;
        }
        if (index < 0) {
          direction = -1;
          oscLeg = { fromTotal: 1 / N, toTotal: 0, index: -1 };
          legSpan(0, 0);
          currentTween = anime.animate(driver, {
            x: 0, y: 0,
            duration: durFor(-1) * 1000, ease: animeEase(easeFor(-1)),
            onUpdate: apply,
            onComplete: () => { emitWaypoint(-1); direction = 1; index = 0; hold(step, -1); },
          });
          el._oscillaTransAnim = currentTween;
          return;
        }
      } else {
        // once — arrived, stay put
        el._oscillaTransAnim = null;
        return;
      }
    }

    const d = deltas[index];
    const legIndex = index;
    oscLeg = {
      // forwards: waypoint i-1 (or home) → i; backwards: i+1 → i
      fromTotal: direction > 0 ? legIndex / N : (legIndex + 2) / N,
      toTotal: (legIndex + 1) / N,
      index: legIndex,
    };
    legSpan(d.dx, d.dy);
    currentTween = anime.animate(driver, {
      x: d.dx, y: d.dy,
      duration: durFor(legIndex) * 1000, ease: animeEase(easeFor(legIndex)),
      onUpdate: apply,
      onComplete: () => { emitWaypoint(legIndex); index += direction; hold(step, legIndex); },
    });
    el._oscillaTransAnim = currentTween;
  }

  function hold(next, wpIndex) {
    const h = holdFor(wpIndex);
    if (h > 0) el._oscillaTransTimer = setTimeout(next, h * 1000);
    else next();
  }

  // Jump to whole-list progress t: pick the leg, rebuild its tween via step()
  // (which reads index/direction), then seek within it. Drift correction from
  // the leader lands here through cfg._anim.setPhase.
  function seekTo(t, dir) {
    const { index: i, frac, direction: d } = legForT(t, dir ?? direction, N);
    currentTween?.pause?.();
    clearTimeout(el._oscillaTransTimer);
    direction = d;
    // step() handles index < 0 / >= N as the mode-specific turnaround, so a
    // home-bound leg is reached by asking for the position past the ends.
    index = (d < 0 && i < 0) ? -1 : i;
    step();
    if (currentTween) currentTween.progress = Math.max(0, Math.min(1, frac));
    cfg._currentT = t;
    cfg._phase = t;
    cfg._alternateDir = direction;
  }

  // Observer pause/resume + network sync interface (see animSync.js)
  cfg._anim = {
    pause() {
      paused = true;
      currentTween?.pause?.();
      clearTimeout(el._oscillaTransTimer);
    },
    /**
     * Retire this run for good. Pausing is not enough: the tween lives on in
     * this closure, and the observer resuming an offscreen element would
     * play it again — after a scrub-back reset had already put the element
     * home — and the leg would run to its waypoint and PARK there, displaced,
     * with nothing left to move it.
     */
    stop() {
      paused = true;
      cfg._stopped = true;
      currentTween?.pause?.();
      currentTween = null;
      clearTimeout(el._oscillaTransTimer);
    },
    play() {
      if (cfg._stopped) return;      // retired by a reset — never revive it
      if (!paused) return;
      paused = false;
      if (currentTween && currentTween.paused) currentTween.play();
      else step();
    },
    setPhase(t) { seekTo(t, cfg._alternateDir ?? direction); },
    setDirection(d) { direction = d >= 0 ? 1 : -1; cfg._alternateDir = direction; },
    getPhase() { return cfg._currentT ?? 0; },
  };

  // Late joiners and remote resumes arrive with a position to start from
  // (animShared.applyRemoteArmSync sets _dragResumeT before calling _start).
  if (cfg._dragResumeT != null) {
    const t = cfg._dragResumeT;
    delete cfg._dragResumeT;
    seekTo(t, cfg._alternateDir ?? 1);
  } else {
    // A fresh run heads out. Without this a -1 left by the previous cycle's
    // home leg would steer the first drift correction onto the home leg.
    cfg._alternateDir = 1;
    step();
  }
}

/** Reset a trans element to its authored position and stop its run. */
export function resetTrans(el, cfg) {
  cfg._stopped = true;
  // Drop the tween inside runTransSequence's closure too — pausing the one
  // on the element leaves the closure's copy resumable. See _anim.stop().
  cfg._anim?.stop?.();
  el._oscillaTransAnim?.pause?.();
  clearTimeout(el._oscillaTransTimer);
  el._oscillaTransAnim = null;
  el.style.translate = "";
  if (el._oscillaTransWrapper) el._oscillaTransWrapper.style.translate = "";
}
